import { useEffect, useState } from 'react'
import { ordersApi, type OrderDetail } from '../lib/api'
import { defaultPrinter, printReceipt, probeAgent, type PrinterConfig } from '../lib/agent'
import { db, kvGet, saveEInvoice, voidPaidOrder, type Order, type OrderEInvoice } from '../lib/db'
import { einvoiceApi, invoiceLabel, statusText } from '../lib/einvoice'
import { clock, dayMonth, money, qtyToText } from '../lib/money'
import { useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { Sheet } from './Sheet'

const VOID_REASONS = ['Rung nhầm bill', 'Khách trả lại hàng', 'Khách đổi món', 'Máy in lỗi, in lại']

interface Props {
  orderId: string
  onClose: () => void
  onVoided: () => void | Promise<void>
}

export function OrderDetailSheet({ orderId, onClose, onVoided }: Props) {
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const [localOrder, setLocalOrder] = useState<Order | null>(null)
  const [printer, setPrinter] = useState<PrinterConfig | null>(null)
  const [invoice, setInvoice] = useState<OrderEInvoice | null>(null)
  const [voiding, setVoiding] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true

    void (async () => {
      // Bản trên máy hiện ra trước để mở là thấy ngay, rồi mới hỏi máy chủ.
      const mine = await db.orders.get(orderId)
      if (!alive) return
      if (mine) {
        setLocalOrder(mine)
        setDetail(fromLocal(mine))
        setInvoice(mine.einvoice ?? null)
      }

      try {
        const remote = await ordersApi.get(orderId)
        // Đơn vừa huỷ trên máy mà chưa đẩy lên thì bản máy chủ đang cũ hơn — giữ bản máy.
        if (alive && !(mine?.voidedAt && !remote.voidedAt)) setDetail(remote)
      } catch {
        /* mất mạng: bản trên máy là đủ */
      }

      // Hỏi lại trạng thái hoá đơn: tờ lúc bán còn nằm trong hàng đợi thì giờ đã có mã rồi.
      try {
        const e = await einvoiceApi.forOrder(orderId)
        if (!alive) return

        const snapshot: OrderEInvoice = {
          status: e.status,
          templateCode: e.templateCode,
          series: e.series,
          invoiceNo: e.invoiceNo,
          taxAuthorityCode: e.taxAuthorityCode,
          lookupUrl: e.lookupUrl,
          issuedAt: e.issuedAt,
          error: e.error,
        }
        setInvoice(snapshot)
        await saveEInvoice(orderId, snapshot)
        setLocalOrder((prev) => (prev ? { ...prev, einvoice: snapshot } : prev))
      } catch {
        /* đơn không có hoá đơn điện tử, hoặc mất mạng — cả hai đều không phải lỗi */
      }
    })()

    void (async () => {
      const saved = await kvGet<PrinterConfig>('printer')
      if (saved?.address && (await probeAgent())) setPrinter(saved ?? defaultPrinter)
    })()

    return () => {
      alive = false
    }
  }, [orderId])

  async function doVoid() {
    const text = reason.trim()
    if (!text) {
      say('Chọn hoặc ghi lý do huỷ')
      return
    }

    setBusy(true)
    await voidPaidOrder(orderId, text)
    try {
      await ordersApi.void(orderId, text)
    } catch {
      // Đã ghi trên máy rồi; đồng bộ đẩy lên sau. Máy chủ nhận đúng chuyển trạng thái này.
      void pushNow()
    }
    setBusy(false)
    await onVoided()
  }

  if (!detail) return <Sheet title="Hoá đơn" onClose={onClose}><p className="sheet-note">Đang mở…</p></Sheet>

  const isVoid = !!detail.voidedAt

  if (voiding) {
    return (
      <Sheet title="Huỷ hoá đơn" onClose={() => setVoiding(false)}>
        <p className="sheet-note">
          {detail.code} · {money(detail.total)}đ. Hoá đơn không bị xoá, chỉ đánh dấu đã huỷ kèm lý
          do — để sau này còn đối chiếu được.
        </p>

        <div className="field">
          <span>Vì sao huỷ</span>
          <div className="chip-row">
            {VOID_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                className="chip"
                aria-pressed={reason === r}
                onClick={() => setReason(r)}
              >
                {r}
              </button>
            ))}
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Hoặc gõ lý do khác"
            maxLength={200}
          />
        </div>

        <div className="sheet-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setVoiding(false)}>
            Quay lại
          </button>
          <button
            type="button"
            className="btn btn-danger-ghost grow"
            onClick={doVoid}
            disabled={busy || !reason.trim()}
          >
            {busy ? 'Đang huỷ…' : 'Huỷ hoá đơn'}
          </button>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet title={detail.code} onClose={onClose}>
      {isVoid && (
        <div className="void-banner">
          Đã huỷ {detail.voidedAt ? `lúc ${clock(detail.voidedAt)} ${dayMonth(detail.voidedAt)}` : ''}
          {detail.voidReason ? ` — ${detail.voidReason}` : ''}
        </div>
      )}

      <div className="pay-row">
        <span>{detail.tableName ?? 'Bán nhanh'}</span>
        <b>{detail.paidAt ? `${clock(detail.paidAt)} ${dayMonth(detail.paidAt)}` : '—'}</b>
      </div>

      <div className="list">
        {detail.lines.map((l, i) => (
          <div key={i} className="list-row">
            <span className="list-name">
              {l.name}
              {l.optionLabel ? ` · ${l.optionLabel}` : ''}
              <small>
                {qtyToText(l.qtyMilli)} × {money(l.unitPrice)}
                {l.note ? ` · ${l.note}` : ''}
              </small>
            </span>
            <span className="list-amount num">{money(l.lineTotal)}</span>
          </div>
        ))}
      </div>

      <div className="card">
        {detail.discount > 0 && (
          <>
            <div className="pay-row">
              <span>Tạm tính</span>
              <b className="num">{money(detail.subtotal)}</b>
            </div>
            <div className="pay-row">
              <span>Giảm giá</span>
              <b className="num">−{money(detail.discount)}</b>
            </div>
          </>
        )}
        <div className="pay-row big">
          <span>Tổng cộng</span>
          <b className="num">{money(detail.total)}</b>
        </div>
        <div className="pay-row">
          <span>{detail.paymentMethod === 'Transfer' ? 'Chuyển khoản' : 'Tiền mặt'}</span>
          <b className="num">{money(detail.amountGiven || detail.total)}</b>
        </div>
        {detail.changeGiven > 0 && (
          <div className="pay-row">
            <span>Tiền thối</span>
            <b className="num">{money(detail.changeGiven)}</b>
          </div>
        )}
      </div>

      {invoice && (
        <div className="card">
          <div className="pay-row">
            <span>Hoá đơn điện tử</span>
            <b>{statusText[invoice.status]}</b>
          </div>
          {invoiceLabel(invoice) && (
            <div className="pay-row">
              <span>Ký hiệu · số</span>
              <b className="num">{invoiceLabel(invoice)}</b>
            </div>
          )}
          {invoice.taxAuthorityCode && (
            <p className="note">Mã cơ quan thuế {invoice.taxAuthorityCode}</p>
          )}
          {invoice.error && <p className="note">{invoice.error}</p>}
        </div>
      )}

      <p className="note">
        Bán ở máy {detail.deviceId ?? '—'}
        {localOrder && localOrder.synced === 0 ? ' · chưa gửi lên máy chủ' : ''}
      </p>

      <div className="sheet-actions">
        {printer && localOrder && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={async () => {
              try {
                await printReceipt(localOrder, shop, printer)
                say('Đã in lại')
              } catch (e) {
                say(e instanceof Error ? e.message : 'Không in được')
              }
            }}
          >
            In lại
          </button>
        )}
        {!isVoid && (
          <button
            type="button"
            className="btn btn-danger-ghost grow"
            onClick={() => {
              setReason('')
              setVoiding(true)
            }}
          >
            Huỷ hoá đơn
          </button>
        )}
      </div>
    </Sheet>
  )
}

function fromLocal(o: Order): OrderDetail {
  return {
    id: o.id,
    code: o.code,
    tableName: o.tableName,
    status: o.status,
    openedAt: o.openedAt,
    paidAt: o.paidAt,
    subtotal: o.subtotal,
    discount: o.discount,
    total: o.total,
    paymentMethod: o.paymentMethod,
    amountGiven: o.amountGiven,
    changeGiven: o.changeGiven,
    customerName: o.customerName,
    note: o.note,
    deviceId: o.deviceId,
    voidedAt: o.voidedAt,
    voidReason: o.voidReason,
    lines: o.items.map((i) => ({
      name: i.name,
      optionLabel: i.optionLabel,
      qtyMilli: i.qtyMilli,
      unitPrice: i.unitPrice,
      lineTotal: i.lineTotal,
      note: i.note,
    })),
  }
}
